// src/pages/common-components/Banner-Popup/BannerPopup.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useBanner } from '../../Hooks/useBanner';

const SESSION_KEY  = 'banner_popup_shown';
const SUPPRESS_KEY = 'banner_popup_suppressed';

/* ─── Styles ─────────────────────────────────────────────────────────────── */
const popupStyles = `
  /* ── Overlay ── */
  .bp-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: rgba(10, 8, 6, 0.72);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    opacity: 0;
    transition: opacity 0.45s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .bp-overlay.bp-overlay--visible {
    opacity: 1;
  }
  .bp-overlay.bp-overlay--exit {
    opacity: 0;
    pointer-events: none;
  }

  /* ── Dialog shell ── */
  .bp-dialog {
    position: relative;
    max-width: 680px;
    width: 100%;
    background: #fff;
    outline: 1px solid rgba(180, 155, 110, 0.35);
    outline-offset: 6px;
    transform: translateY(28px) scale(0.97);
    transition:
      transform 0.5s cubic-bezier(0.16, 1, 0.3, 1),
      opacity   0.5s cubic-bezier(0.16, 1, 0.3, 1);
    opacity: 0;
  }
  .bp-overlay--visible .bp-dialog {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
  .bp-overlay--exit .bp-dialog {
    transform: translateY(-16px) scale(0.97);
    opacity: 0;
  }

  /* ── Image wrap ── */
  .bp-image-wrap {
    position: relative;
    width: 100%;
    overflow: hidden;
    background: #f5f2ee;
    min-height: 260px;
    max-height: 520px;
  }

  /* ── Spinner (shown while image loads) ── */
  .bp-spinner-wrap {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    background: #f5f2ee;
    transition: opacity 0.3s ease;
    z-index: 1;
  }
  .bp-spinner-wrap--hidden {
    opacity: 0;
    pointer-events: none;
  }
  .bp-spinner {
    width: 36px;
    height: 36px;
    border: 2.5px solid rgba(180, 155, 110, 0.25);
    border-top-color: #b49b6e;
    border-radius: 50%;
    animation: bp-spin 0.8s linear infinite;
  }
  @keyframes bp-spin {
    to { transform: rotate(360deg); }
  }
  .bp-spinner-text {
    font-size: 10px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #b49b6e;
    font-weight: 500;
  }

  /* ── Banner image ── */
  .bp-image {
    width: 100%;
    display: block;
    object-fit: cover;
    opacity: 0;
    transition: opacity 0.4s ease;
  }
  .bp-image--loaded {
    opacity: 1;
  }

  /* ── Close button ── */
  .bp-close {
    position: absolute;
    top: -14px;
    right: -14px;
    z-index: 10;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 1px solid rgba(180, 155, 110, 0.5);
    background: #fff;
    color: #5a4a3a;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 18px;
    line-height: 1;
    transition: background 0.25s ease, color 0.25s ease, transform 0.25s ease;
    box-shadow: 0 2px 10px rgba(0,0,0,0.12);
  }
  .bp-close:hover {
    background: #5a4a3a;
    color: #fff;
    transform: scale(1.08) rotate(90deg);
  }
  .bp-close:focus-visible {
    outline: 2px solid #b49b6e;
    outline-offset: 2px;
  }

  /* ── Footer strip ── */
  .bp-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    border-top: 1px solid #f0ece6;
    background: #faf9f7;
  }
  .bp-footer-label {
    font-size: 10px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: #b49b6e;
    font-weight: 500;
  }
  .bp-dismiss-check {
    display: flex;
    align-items: center;
    gap: 7px;
    cursor: pointer;
    user-select: none;
  }
  .bp-dismiss-check input[type="checkbox"] {
    accent-color: #b49b6e;
    width: 14px;
    height: 14px;
    cursor: pointer;
  }
  .bp-dismiss-check span {
    font-size: 11px;
    letter-spacing: 0.05em;
    color: #888;
  }

  /* ── Mobile ── */
  @media (max-width: 576px) {
    .bp-dialog {
      max-width: 100%;
      outline-offset: 4px;
    }
    .bp-close {
      top: -12px;
      right: -8px;
    }
    .bp-footer {
      flex-direction: column;
      gap: 8px;
      align-items: flex-start;
    }
    .bp-image-wrap {
      min-height: 180px;
    }
  }
`;

/* ─── Component ─────────────────────────────────────────────────────────── */
const BannerPopup: React.FC = () => {
  const { data: banner, isLoading } = useBanner();

  const [visible, setVisible]     = useState(false);
  const [exiting, setExiting]     = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [suppress, setSuppress]   = useState(false);

  /* Show popup as soon as we have a URL — spinner covers the image wait ---- */
  useEffect(() => {
    if (isLoading) return;
    if (!banner?.imageUrl) return;

    const suppressed = localStorage.getItem(SUPPRESS_KEY) === 'true';
    if (suppressed) return;

    const shownThisSession = sessionStorage.getItem(SESSION_KEY) === 'true';
    if (shownThisSession) return;

    sessionStorage.setItem(SESSION_KEY, 'true');

    // Open immediately — spinner shown until image fires onLoad
    setVisible(true);
  }, [banner, isLoading]);

  /* Close ----------------------------------------------------------------- */
  const handleClose = useCallback(() => {
    if (suppress) {
      localStorage.setItem(SUPPRESS_KEY, 'true');
    }
    setExiting(true);
    setTimeout(() => {
      setVisible(false);
      setExiting(false);
    }, 450);
  }, [suppress]);

  /* Keyboard close -------------------------------------------------------- */
  useEffect(() => {
    if (!visible) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [visible, handleClose]);

  /* Body scroll lock ------------------------------------------------------ */
  useEffect(() => {
    document.body.style.overflow = visible ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [visible]);

  if (!visible || !banner?.imageUrl) return null;

  return (
    <>
      <style>{popupStyles}</style>

      <div
        className={`bp-overlay${visible && !exiting ? ' bp-overlay--visible' : ''}${exiting ? ' bp-overlay--exit' : ''}`}
        onClick={handleClose}
        role="dialog"
        aria-modal="true"
        aria-label="Promotional banner"
      >
        <div className="bp-dialog" onClick={(e) => e.stopPropagation()}>

          {/* Close button */}
          <button className="bp-close" onClick={handleClose} aria-label="Close banner">
            ×
          </button>

          {/* Image area */}
          <div className="bp-image-wrap">

            {/* Spinner — fades out once image is ready */}
            <div
              className={`bp-spinner-wrap${imgLoaded ? ' bp-spinner-wrap--hidden' : ''}`}
              aria-hidden={imgLoaded}
            >
              <div className="bp-spinner" />
              <span className="bp-spinner-text">Loading</span>
            </div>

            {/* Image — fades in once loaded */}
            <img
              src={banner.imageUrl}
              alt="Promotional banner"
              className={`bp-image${imgLoaded ? ' bp-image--loaded' : ''}`}
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgLoaded(true)}
              draggable={false}
            />
          </div>

          {/* Footer */}
          <div className="bp-footer">
            <span className="bp-footer-label"></span>
            <label className="bp-dismiss-check">
              <input
                type="checkbox"
                checked={suppress}
                onChange={(e) => setSuppress(e.target.checked)}
              />
              <span>Don't show again</span>
            </label>
          </div>

        </div>
      </div>
    </>
  );
};

export default BannerPopup;